import React, { useEffect, useRef, useState } from "react";
import Style from "./style.module.css";
import { ActivitySquareIcon, ChevronDownIcon, HistoryIcon } from "lucide-react";
import { Alert, Skeleton, Snackbar } from "@mui/material";
import { useApiCall } from "../../../../../component/custom-hooks/api-call";
import InnerLoader from "../../../../../component/ui/loader/inner-loader";
import DataNotFound from "../../../../../component/ui/loader/data-not-found";
import * as XLSX from "xlsx";
import {
  Table,
  TableBody,
  TableCard,
  TableCell,
  TableFilterReturn,
  TableFooter,
  TableHead,
  TableHeaderWrap,
  TableRow,
} from "../../../../../component/table/table-layout";
import DynamicFilter from "../../../../../component/table/filter-table/dynamic-filter";
import CustomizeTabsPopup from "../../../../../component/table/customize-table";
import LayoutTopIcon from "../../../../../component/icons/layout-top";
import FilterFunnel01Icon from "../../../../../component/icons/filter-funnel-01";
import XCloseIcon from "../../../../../component/icons/x-close";
import Tooltip from "../../../../../component/ui/tooltip";
import Swal from "sweetalert2";
import FileCode1Icon from "../../../../../component/icons/file-code-1";
import { CustomDropdownMenuList } from "../../../../../component/ui/dropdown/CustomDropdownMenuList";
import { CustomDropdownMenu } from "../../../../../component/ui/dropdown/CustomDropdownMenu";
import { CustomDropdownButton } from "../../../../../component/ui/dropdown/CustomDropdownButton";
import DotsVerticalIcon from "../../../../../component/icons/dots-vertical";
import { CustomDropdown } from "../../../../../component/ui/dropdown/CustomDropdown";
import StatusChip from "../../../../../component/ui/chip/status";

const Device = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [apiData, setApiData] = useState([]);
  const [showModalFilterTable, setShowModalFilterTable] = useState(false);
  const [filterReturn, setFilterReturn] = useState([]);
  const [saveFilter, setSaveFilter] = useState([]);
  const [tablePagination, setTablePagination] = useState({});
  const [btnLoader, setBtnLoader] = useState(false);
  const [alertData, setAlertData] = useState({ isShow: false });
  const [tableHeader, setTableHeader] = useState([]);
  const [tableFooter, setTableFooter] = useState([]);
  const [showModalCustomizeTabs, setShowModalCustomizeTabs] = useState(false);
  const [exportTo, setExportTo] = useState(false);

  const apiCall = useApiCall();
  const tableRef = useRef(null);

  useEffect(() => {
    getData({});
  }, []);

  const getData = async ({
    filter = filterReturn,
    page,
    page_quantity = 10,
  }) => {
    if (!page) setIsLoading(true);

    let extraUrl = `?page=${page || 1}&page_quantity=${page_quantity}`;
    for (let i = 0; i < filter?.length; i++) {
      extraUrl += `&${filter[i]?.name}=${filter[i]?.value}`;
    }

    try {
      const res = await apiCall({
        method: "get",
        url: `list-sessions${extraUrl}`,
      });

      if (page) {
        setApiData((prev) => ({
          ...prev,
          active: [
            ...(prev.active || []),
            ...(res?.data?.active_sessions || []),
          ],
          history: [
            ...(prev.history || []),
            ...(res?.data?.history_sessions || []),
          ],
        }));
      } else {
        setApiData({
          active: res?.data?.active_sessions || [],
          history: res?.data?.history_sessions || [],
        });
      }

      setTableHeader(res?.applied_headers || []);
      setTableFooter(res?.footer || []);
      setSaveFilter(res?.custom_filters || []);
      setTablePagination(res?.pagination || {});
      setBtnLoader(false);
      setIsLoading(false);
    } catch (error) {
      console.warn("Error :", error);
      setIsLoading(false);
    }
  };

  const handleLogout = async (tokenId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to logout this device?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, logout",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const res = await apiCall({
          method: "post",
          url: `logout-specific/${tokenId}`,
        });
        if (res.status === 200) {
          Swal.fire({
            icon: "success",
            title: "Logged out!",
            text: "The device has been logged out successfully.",
          });

          getData({});
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.res?.data?.message || "Something went wrong.",
        });
      }
    }
  };

  // 🔹 Export Sessions Data (CSV / Excel)
  const handleExport = async (type, localData = null) => {
    try {
      const sessionsData =
        localData && localData.length
          ? localData
          : [...(apiData.active || []), ...(apiData.history || [])];

      if (!sessionsData.length) {
        Swal.fire({
          title: "Warning!",
          text: "No session data available to export.",
          icon: "warning",
          confirmButtonText: "OK",
        });
        return;
      }

      const exportData = sessionsData.map((item) => ({
        Device: item.device || "--",
        "IP Address": item.ip_address || "--",
        Location: item.location || "--",
        "Login Time": item.logged_in_at || "--",
        "Logout Time": item.logged_out_at || "--",
        Status: item.is_active ? "Active" : "Inactive",
      }));

      const fileName = `sessions_${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, "-")}`;

      if (type === "csv") {
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const csvContent = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${fileName}.csv`;
        link.click();
      } else {
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sessions");
        XLSX.writeFile(workbook, `${fileName}.xlsx`);
      }

      Swal.fire({
        title: "Success!",
        text: "Sessions data exported successfully.",
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Export Error:", error);
      Swal.fire({
        title: "Error!",
        text: "Something went wrong while exporting sessions.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleClose = () => setAlertData({ isShow: false });

  const removeFilter = (index) => {
    const updated = [...filterReturn];
    updated.splice(index, 1);
    setFilterReturn(updated);
    getData({ filter: updated });
  };

  return (
    <>
      <div className={Style.mainBody}>
        <div className={Style.teamSpaceDashboard}>
          <div className={Style.teamSpaceDashboard_main}>
            <div className={Style.teamSpaceDashboard_body}>
              {/* HEADER */}
              <div className={Style.empDirectory_header}>
                <div className={Style.empDirectory_header_left}>
                  {/* {filterReturn?.length ? (
                    <div className={Style.filterContainer}>
                      <p className={Style.searchText}>Search By</p>
                      {filterReturn.map((eachFilter, i) => (
                        <div className={Style.eachFilter} key={i}>
                          <p onClick={() => setShowModalFilterTable(true)}>
                            <span>
                              {eachFilter?.name?.replaceAll("_", " ")} :{" "}
                            </span>
                            <b>{eachFilter?.selectedOperator}</b>
                            <em>{eachFilter?.value}</em>
                          </p>
                          <span
                            className={Style.searchClosedBtn}
                            onClick={() => removeFilter(i)}
                          >
                            <div className={Style.icon}>
                              <XCloseIcon />
                            </div>
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null} */}
                  <TableFilterReturn
                    filterReturn={filterReturn}
                    onChange={(allFilter) => {
                      setFilterReturn(allFilter);
                      getData({ filter: allFilter });
                    }}
                  />
                </div>

                {/* ACTION BUTTONS */}
                <div className={Style.empDirectory_header_right}>
                  <div className={Style.bpfl_action}>
                    {/* <Tooltip content="Table Filter" position="left center">
                      <div
                        className={`${Style.bpfl_actionBtn} ${
                          filterReturn?.length ? Style.active : ""
                        }`}
                        onClick={() => setShowModalFilterTable(true)}
                      >
                        <div className={Style.icon}>
                          <FilterFunnel01Icon />
                        </div>
                      </div>
                    </Tooltip> */}

                    <CustomDropdown position="bottom-right">
                      <CustomDropdownButton arrow={false}>
                        <DotsVerticalIcon />
                      </CustomDropdownButton>

                      <CustomDropdownMenu>
                        <CustomDropdownMenuList
                          className={Style.CustomDropdownMenuList}
                          closed={false}
                        >
                          <div
                            className={Style.CustomDropdownMenuListBtn}
                            onClick={() => setExportTo(!exportTo)}
                          >
                            <div>
                              <LayoutTopIcon /> Export to
                            </div>
                            <div
                              className={`${Style.dropArrow} ${
                                exportTo ? Style.isActive : ""
                              }`}
                            >
                              <ChevronDownIcon />
                            </div>
                          </div>

                          {exportTo && (
                            <div className={Style.subItemMenu}>
                              <CustomDropdownMenuList
                                className={Style.subItemList}
                                onClick={() =>
                                  handleExport("csv", [
                                    ...apiData.active,
                                    ...apiData.history,
                                  ])
                                }
                              >
                                <div className="d-flex align-items-center gap-1">
                                  <FileCode1Icon /> CSV
                                </div>
                              </CustomDropdownMenuList>

                              <CustomDropdownMenuList
                                className={Style.subItemList}
                                onClick={() =>
                                  handleExport("excel", [
                                    ...apiData.active,
                                    ...apiData.history,
                                  ])
                                }
                              >
                                <div className="d-flex align-items-center gap-1">
                                  <FileCode1Icon /> XLSX
                                </div>
                              </CustomDropdownMenuList>
                            </div>
                          )}
                        </CustomDropdownMenuList>
                      </CustomDropdownMenu>
                    </CustomDropdown>
                  </div>
                </div>
              </div>

              {/* BODY */}
              <div className={Style.empDirectory_body}>
                {isLoading ? (
                  <InnerLoader />
                ) : !apiData?.active?.length && !apiData?.history?.length ? (
                  <div className="flex justify-center items-center w-full h-full">
                    <DataNotFound title={"Sessions"} />
                  </div>
                ) : (
                  <TableCard ref={tableRef}>
                    <Table>
                      {/* TABLE HEAD */}
                      <TableHead>
                        <TableRow>
                          <TableCell header sticky>
                            <div className="flex gap-2 items-center">
                              <TableHeaderWrap>
                                <span className="pl-[50px]">Device Info</span>
                              </TableHeaderWrap>
                            </div>
                          </TableCell>

                          {tableHeader?.map((head, i) =>
                            head.is_visible === 1 && head.show === 1 ? (
                              <TableCell header key={i}>
                                <TableHeaderWrap>{head.value}</TableHeaderWrap>
                              </TableCell>
                            ) : null,
                          )}
                        </TableRow>
                      </TableHead>

                      {/* TABLE BODY */}
                      <TableBody>
                        {/* ACTIVE SESSIONS */}
                        {apiData.active?.length > 0 && (
                          <>
                            <TableRow className="bg-gray-100 !font-bold text-center">
                              <TableCell colSpan={tableHeader?.length}>
                                <div className="flex justify-center items-center gap-1">
                                  <ActivitySquareIcon className="w-4 h-4" />
                                  Active Sessions
                                </div>
                              </TableCell>
                            </TableRow>

                            {apiData.active.map((session, index) => (
                              <TableRow
                                key={`active-${index}`}
                                className={
                                  session.is_current_device
                                    ? Style.currentDeviceRow
                                    : ""
                                }
                              >
                                <TableCell sticky>
                                  <div
                                    className={`${Style.tableActionArea} !gap-4`}
                                  >
                                    <div className="font-medium leading-5">
                                      {session?.device}
                                    </div>
                                  </div>
                                </TableCell>
                                {tableHeader?.map((head, i) =>
                                  head.is_visible === 1 && head.show === 1 ? (
                                    <TableCell key={`${head.key}-${i}`}>
                                      {head.key === "is_current_device" ? (
                                        session.is_current_device ? (
                                          <div className="text-center capitalize">
                                            <StatusChip color="green">
                                              This Device
                                            </StatusChip>
                                          </div>
                                        ) : (
                                          <div className="text-center capitalize">
                                            {/* // Show logout button if not current device */}
                                            <button
                                              className={Style.logoutButton}
                                              onClick={() =>
                                                handleLogout(session.token_id)
                                              }
                                            >
                                              <StatusChip color="red">
                                                Logout
                                              </StatusChip>
                                            </button>
                                          </div>
                                        )
                                      ) : (
                                        (session[head.key] ?? "--")
                                      )}
                                    </TableCell>
                                  ) : null,
                                )}
                              </TableRow>
                            ))}
                          </>
                        )}

                        {/* HISTORY SESSIONS */}
                        {apiData.history?.length > 0 && (
                          <>
                            <TableRow className="bg-gray-100 !font-bold text-center">
                              <TableCell colSpan={tableHeader?.length}>
                                <div className="flex justify-center items-center gap-1">
                                  <HistoryIcon className="w-4 h-4" />
                                  Session History
                                </div>
                              </TableCell>
                            </TableRow>

                            {apiData.history.map((session, index) => (
                              <TableRow key={`history-${index}`}>
                                <TableCell sticky>
                                  <div
                                    className={`${Style.tableActionArea} !gap-4`}
                                  >
                                    <div className="font-medium leading-5">
                                      {session?.device}
                                    </div>
                                  </div>
                                </TableCell>
                                {tableHeader?.map((head, i) =>
                                  head.is_visible === 1 && head.show === 1 ? (
                                    <TableCell key={`${head.key}-${i}`}>
                                      {head.key === "is_current_device"
                                        ? session.is_current_device
                                          ? "--"
                                          : // Show logout button if not current device
                                            "--"
                                        : (session[head.key] ?? "--")}
                                    </TableCell>
                                  ) : null,
                                )}
                              </TableRow>
                            ))}
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </TableCard>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FILTER MODAL */}
      {showModalFilterTable && (
        <DynamicFilter
          data={[]}
          onClose={() => setShowModalFilterTable(false)}
          returnData={filterReturn}
          onSave={(filters) => {
            getData({ filter: filters });
            setShowModalFilterTable(false);
            setFilterReturn(filters);
          }}
          saveFilter={saveFilter}
          apiUrl="list-sessions"
        />
      )}

      {/* CUSTOMIZE COLUMNS MODAL */}
      {showModalCustomizeTabs && (
        <CustomizeTabsPopup
          data={tableHeader}
          onClose={() => setShowModalCustomizeTabs(false)}
          onPreview={(headers) => setTableHeader(headers)}
          onSave={(headers) => setTableHeader(headers)}
          apiUrl="list-sessions"
        />
      )}

      {/* ALERTS */}
      <Snackbar
        open={alertData?.isShow}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={alertData?.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertData?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Device;
